import { useEffect, useState } from "react";
import "./po.css";
import html2pdf from "html2pdf.js";
import { useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

// ✅ NUMBER TO WORDS UTILITY UTILS
const numberToWords = (num) => {
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const c = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

  if (num === 0) return "Zero";
  if (num < 10) return a[num];
  if (num < 20) return c[num - 10];
  if (num < 100) return b[Math.floor(num / 10)] + " " + a[num % 10];
  if (num < 1000) return a[Math.floor(num / 100)] + " Hundred " + numberToWords(num % 100);
  if (num < 100000) return numberToWords(Math.floor(num / 1000)) + " Thousand " + numberToWords(num % 1000);
  if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + " Lakh " + numberToWords(num % 100000);
  return num;
};

const formatPaymentTerm = (po) => {
  if (po.payment_type === "Advance Payment") {
    if ((Array.isArray(po.payment_stages) && po.payment_stages.length > 0) || (typeof po.payment_stages === "string" && po.payment_stages !== "[]")) return "As per payment stages";
    return `${po.advance_percent}% Advance, ${100 - Number(po.advance_percent || 0)}% After Delivery`;
  }
  if (po.payment_type === "Credit Payment") return po.credit_days ? String(po.credit_days) : "Credit Payment";
  if (po.payment_type === "Running Payment") return `Running Payment - ${po.running_basis || "As specified in stages"}`;
  if (po.payment_type === "Full Payment") return "100% Full Payment";
  return po.payment_term || "Not specified";
};

const getPaymentStages = (po) => {
  if (Array.isArray(po.payment_stages)) return po.payment_stages;
  if (typeof po.payment_stages === "string") {
    try {
      const stages = JSON.parse(po.payment_stages);
      return Array.isArray(stages) ? stages : [];
    } catch (_) {
      return [];
    }
  }
  return [];
};

const getAdditionalTerms = (po) => {
  let terms = po.additional_terms;
  if (typeof terms === "string") {
    try {
      terms = JSON.parse(terms);
    } catch (_) {
      terms = [];
    }
  }
  return Array.isArray(terms)
    ? terms.map((term) => String(term || "").trim()).filter(Boolean)
    : [];
};

function POPrintPage({ poId }) {
  const [po, setPo] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ccEmails, setCcEmails] = useState("");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentRole = String(currentUser.role || "").toLowerCase().replace(/[\s-]+/g, "_");
  const canApproveManualPo = ["admin", "super_admin", "manager"].includes(currentRole);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.isDraft) {
      setPo(location.state.draftData);
      setIsDraft(true);
    } else {
      authFetch(`${process.env.REACT_APP_API_URL}/po/${poId}`)
        .then((res) => res.json())
        .then((data) => {
          setPo(data);
          setIsDraft(false);
        })
        .catch(err => console.error("Error retrieving system storage entity profiles:", err));
    }
  }, [poId, location.state]);

  useEffect(() => {
    const approvalUrl = po?.comparison_id
      ? `${process.env.REACT_APP_API_URL}/api/comparison/${po.comparison_id}/approval-status`
      : po?.id && String(po.id) !== "0"
        ? `${process.env.REACT_APP_API_URL}/api/po/${po.id}/approval-status`
        : null;
    if (!approvalUrl) return undefined;
    const checkApproval = async () => {
      try {
        const response = await authFetch(approvalUrl);
        const result = await response.json();
        if (response.ok && result.success) {
          setPo((current) => ({
            ...current,
            approval_status: result.status,
            status: result.po_status || current.status,
            approved_by: result.approved_by || current.approved_by
          }));
        }
      } catch (error) {
        console.error("Approval status check failed:", error);
      }
    };
    checkApproval();
    const timer = setInterval(checkApproval, 10000);
    return () => clearInterval(timer);
  }, [po?.comparison_id, po?.id]);

  if (!po) {
    return <div style={{ padding: "20px" }}>Loading Document Render Canvas Engine...</div>;
  }

  // ✅ TOTAL CALCULATIONS
  const baseAmount = (po.items || []).reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const discountAmount = Math.min(Math.max(Number(po.overall_discount_amount) || 0, 0), baseAmount);
  const subtotal = baseAmount - discountAmount;
  const gstTotal = (po.items || []).reduce((s, i) => s + ((Number(i.qty) || 0) * (Number(i.rate) || 0) * (Number(i.gst_percent) || 0) / 100), 0) * (baseAmount ? subtotal / baseAmount : 0);
  const tds = subtotal * (Number(po.tds_percent) || 0) / 100;
  const cgst = po.tax_mode === "IGST" ? 0 : gstTotal / 2;
  const sgst = po.tax_mode === "IGST" ? 0 : gstTotal / 2;
  const igst = po.tax_mode === "IGST" ? gstTotal : 0;
  const total = subtotal + gstTotal - tds;
  const paymentStages = getPaymentStages(po);
  const additionalTerms = getAdditionalTerms(po);

  const printPO = () => { window.print(); };
  const isApprovalApproved = String(po.approval_status || "Pending").toLowerCase() === "approved"
    && (po.comparison_id || po.approved_by);
  const isApprovalRejected = String(po.approval_status || "Pending").toLowerCase() === "rejected";

  const persistDraftForApproval = async () => {
    const existingId = po.id && String(po.id) !== "0" ? po.id : null;
    if (!existingId) throw new Error("Please save this PO as a draft before sending approval.");
    if (!isDraft) return existingId;
    const response = await authFetch(`${process.env.REACT_APP_API_URL}/po/${existingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        order_id: po.order_id,
        date: po.date || new Date().toISOString().slice(0, 10),
        vendor_id: po.vendor_id,
        site_id: po.site_id,
        total_amount: Math.round(total),
        delivery_address_line1: po.delivery_address_line1,
        delivery_address_line2: po.delivery_address_line2,
        delivery_address_line3: po.delivery_address_line3,
        delivery_contact: po.delivery_contact,
        delivery_phone: po.delivery_phone,
        category: "General",
        // Once approval is requested this is a reviewable PO, not an editable draft.
        status: po.status || "Draft",
        expected_delivery_date: po.date,
        payment_term: po.payment_term || po.payment_type || "",
        payment_type: po.payment_type || "",
        advance_percent: po.advance_percent,
        credit_days: po.credit_days,
        running_basis: po.running_basis || "",
        payment_stages: po.payment_stages || [],
        delivery_date: po.delivery_date || po.date,
        tax_mode: po.tax_mode || "CGST_SGST",
        tds_percent: po.tds_percent || 0,
        overall_discount_amount: discountAmount,
        comparison_id: po.comparison_id || null,
        approval_status: "Pending",
        transportation: po.transportation,
        unloading: po.unloading,
        additional_terms: additionalTerms,
        items: po.items
      })
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error || "Unable to save PO before approval request");
    setPo((current) => ({ ...current, id: existingId, approval_status: "Pending" }));
    setIsDraft(false);
    return existingId;
  };

  const requestPoApproval = async () => {
    if (isProcessing || isApprovalApproved) return;
    const message = window.prompt("Approval message", "Please review and approve this Purchase Order.");
    if (message === null) return;
    setIsProcessing(true);
    try {
      const id = await persistDraftForApproval();
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/po/${id}/request-approval`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message })
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to send approval notification");
      setPo((current) => ({
        ...current,
        status: "Pending Approval",
        approval_status: "Pending",
        approval_requested_at: new Date().toISOString()
      }));
      alert("PO approval notification sent to the approver");
    } catch (error) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const approveManualPo = async () => {
    if (!po.id || String(po.id) === "0") return;
    setIsProcessing(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/po/${po.id}/approve`, { method: "POST" });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to approve PO");
      setPo((current) => ({ ...current, status: "Approved", approval_status: "Approved" }));
      alert("PO approved successfully");
    } catch (error) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const rejectManualPo = async () => {
    if (!po.id || String(po.id) === "0" || isProcessing) return;
    if (!window.confirm("Reject this Purchase Order?")) return;
    setIsProcessing(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/po/${po.id}/reject`, { method: "POST" });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to reject PO");
      setPo((current) => ({ ...current, status: "Rejected", approval_status: "Rejected" }));
      alert("PO rejected");
    } catch (error) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // 🛠️ FIXED: Reliable Promise execution chain for html2pdf conversion
  const generatePDFBlob = () => {
    const element = document.getElementById("po-container");
    const opt = {
      margin: 5,
      filename: `${po.order_id}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: 'css', after: '.no-break-here' } 
    };

    return html2pdf().set(opt).from(element).outputPdf("blob");
  };

  // ⚡ COMMIT AND SAVE PIPELINE ON EXPLICIT ACTIONS
  const sendPO = async () => {
    if (isProcessing) return;
    if (!isApprovalApproved) {
      alert("Send PO is disabled until the authorized approver approves this comparison.");
      return;
    }
    setIsProcessing(true);

    try {
      let finalId = poId;

      // STEP 1: Commit document dataset matrix into relational cloud space if it's currently a draft
      if (isDraft) {
        const existingId = po.id && String(po.id) !== "0" ? po.id : null;
        if (!existingId) throw new Error("Please save this PO as a draft before sending it.");
        console.log("Updating existing PO before sending...");
        const createRes = await authFetch(`${process.env.REACT_APP_API_URL}/po/${existingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            order_id: po.order_id,
            // PostgreSQL date columns reject an empty string; use today when
            // the preview was created without a date.
            date: po.date || new Date().toISOString().slice(0, 10),
            vendor_id: po.vendor_id,
            site_id: po.site_id,
            total_amount: Math.round(total),
            delivery_address_line1: po.delivery_address_line1,
            delivery_address_line2: po.delivery_address_line2,
            delivery_address_line3: po.delivery_address_line3,
            delivery_contact: po.delivery_contact,
            delivery_phone: po.delivery_phone,
            category: "General",
            status: "Ordered",
            expected_delivery_date: po.date,
            payment_term: po.payment_term || po.payment_type || "",
            payment_type: po.payment_type || "",
            advance_percent: po.advance_percent,
            credit_days: po.credit_days,
            running_basis: po.running_basis || "",
            payment_stages: po.payment_stages || [],
            delivery_date: po.delivery_date || po.date,
            tax_mode: po.tax_mode || "CGST_SGST",
            tds_percent: po.tds_percent || 0,
            overall_discount_amount: discountAmount,
            comparison_id: po.comparison_id || null,
            approval_status: po.approval_status || "Pending",
            transportation: po.transportation,
            unloading: po.unloading,
            additional_terms: additionalTerms,
            items: po.items
          })
        });

        if (!createRes.ok) {
          let details = "";
          try {
            const body = await createRes.json();
            details = body.error || body.message || "";
          } catch (_) {}
          throw new Error(`DB Save Failed (${createRes.status})${details ? `: ${details}` : ""}`);
        }
        
        await createRes.json();
        finalId = existingId;
        console.log("Existing PO updated before sending:", finalId);
      }

      // STEP 2: Render PDF matching generation frames securely
      console.log("Generating PDF file artifact wrapper...");
      const pdfBlob = await generatePDFBlob();
      
      const formData = new FormData();
      // Ensure key matches the field name your backend is expecting (e.g., 'pdf')
      formData.append("pdf", pdfBlob, `${po.order_id}.pdf`);
      formData.append("cc_emails", ccEmails);

      // STEP 3: Dispatch final system mailing process arrays
      console.log(`Sending Email via endpoint for PO ID: ${finalId}...`);
      const mailRes = await authFetch(`${process.env.REACT_APP_API_URL}/api/send-po/${finalId}`, {
        method: "POST",
        body: formData
        // Note: Do NOT add 'Content-Type' header manually here when sending FormData. 
        // The browser automatically sets it along with the required multi-part boundary string.
      });

      if (!mailRes.ok) {
        let dispatchError = "";
        try {
          const errorBody = await mailRes.json();
          dispatchError = errorBody.error || errorBody.message || "";
        } catch (_) {
          // Keep the HTTP status when the server does not return JSON.
        }
        throw new Error(`Email Dispatch Endpoint rejected with status ${mailRes.status}${dispatchError ? `: ${dispatchError}` : ""}`);
      }

      const mailData = await mailRes.json();
      console.log("Server dispatch receipt:", mailData);

      localStorage.removeItem("poDraft");
      
      alert("Purchase Order written to database, indexed, and emailed successfully! ✅");
      navigate("/po-list");

    } catch (err) {
      console.error("Critical Failure in SendPO pipeline:", err);
      alert(`Could not complete dispatch pipeline: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", padding: "20px" }}>
      
      {/* ACTION SYSTEM BAR TRIMS */}
      <div className="no-print" style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button onClick={() => navigate(-1)} style={thStyleFixes.backBtn}>← Back</button>
        <button onClick={() => navigate("/purchase-order", { state: { draftId: po.id || poId } })} style={thStyleFixes.backBtn}>✎ Edit PO</button>
        <button onClick={printPO} style={thStyleFixes.printBtn}>🖨 Print / Save PDF</button>
        {isApprovalApproved && <button onClick={sendPO} style={{ ...thStyleFixes.sendBtn, opacity: isProcessing ? 0.5 : 1, cursor: isProcessing ? "not-allowed" : "pointer" }} disabled={isProcessing}>
          {isProcessing ? "Processing & Sending Email..." : "📧 Send PO"}
        </button>}
        {!isApprovalApproved && !isApprovalRejected && !po.approval_requested_at && <button onClick={requestPoApproval} disabled={isProcessing} style={{ ...thStyleFixes.printBtn, opacity: isProcessing ? 0.6 : 1 }}>📨 Send Approval Notification</button>}
        {!po.comparison_id && canApproveManualPo && !isApprovalApproved && !isApprovalRejected && <>
          <button onClick={approveManualPo} disabled={isProcessing || !po.approval_requested_at} style={{ ...thStyleFixes.printBtn, opacity: isProcessing || !po.approval_requested_at ? 0.5 : 1 }}>✅ Approve PO</button>
          <button onClick={rejectManualPo} disabled={isProcessing || !po.approval_requested_at} style={{ ...thStyleFixes.printBtn, background: "#b91c1c", color: "#fff", opacity: isProcessing || !po.approval_requested_at ? 0.5 : 1 }}>❌ Reject PO</button>
        </>}
        <input
          type="text"
          value={ccEmails}
          onChange={(e) => setCcEmails(e.target.value)}
          placeholder="Additional CC emails (optional, comma-separated)"
          className="no-print"
          style={{ minWidth: "250px", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "12px" }}
        />
      </div>
      {!isApprovalApproved && <div className="no-print" style={{ marginBottom: "14px", color: isApprovalRejected ? "#991b1b" : "#92400e", background: isApprovalRejected ? "#fef2f2" : "#fffbeb", border: `1px solid ${isApprovalRejected ? "#fecaca" : "#fde68a"}`, borderRadius: "8px", padding: "10px 12px", fontSize: "12px" }}>
        {isApprovalRejected ? "This PO was rejected and cannot be sent to the vendor." : "Approval required before this PO can be sent to the vendor. The page will refresh automatically after approval."}
      </div>}

      {/* DOCUMENT PREVIEW WRAPPER MATRIX RENDER */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div id="po-container" style={{ 
          width: "210mm", 
          minHeight: "290mm",
          padding: "10mm 10mm 0 10mm",
          boxSizing: "border-box", 
          background: "#ffffff" 
        }}>
          
          {/* HEADER ROW TRIMS */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: "22px", letterSpacing: "0.5px", fontWeight: "600", fontFamily: "serif" }}>PURCHASE ORDER</h1>
              <p style={{ marginTop: "13px", fontFamily: "serif", fontSize: "13px" }}><b>Order No:</b> {po.order_id}</p>
              <p style={{ fontFamily: "serif", fontSize: "13px" }}>
                <b>Date:</b> {po.date ? new Date(po.date).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "-"}
              </p>
            </div>
            <img src="/logo.jpg" alt="logo" style={{ width: "300px", objectFit: "fill" }} />
          </div>

          <hr style={{ margin: "10px 0" }} />

          {/* VENDOR + BILLING TILES */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", marginRight: "1px" }}>
            <div style={{ width: "45%", lineHeight: "1.4", fontFamily: "serif", fontSize: "13px", marginBottom: "3px" }}>
              <b>To:</b>
              <p style={{ margin: "10px 0", fontSize: "13px" }}><b>{po.vendor_name}</b></p>
              <p style={{ margin: "2px 0", fontSize: "12px" }}>{po.vendor_address_line1 || po.address_line1}</p>
              <p style={{ margin: "2px 0", fontSize: "12px" }}>{po.vendor_address_line2 || po.address_line2}</p>
              <p style={{ margin: "2px 0", fontSize: "12px" }}>{po.vendor_address_line3 || po.address_line3}</p>
              <p style={{ margin: "4px 0", fontSize: "12px" }}><b>GST:</b> {po.gst_no}</p>
              <p style={{ margin: "2px 0", fontSize: "12px" }}>{po.contact_person} - {po.contact_number}</p>
            </div>

            <div style={{ width: "45%", fontFamily: "serif", lineHeight: "1.4" }}>
              <p style={{ fontSize: "13px", margin: "0 0 10px 0" }}><b>Billing Name & Address:</b></p>
              <p style={{ marginBottom: "2px", fontSize: "12px" }}><b>Ensileta Interiors & Modular Solutions</b></p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}>No.77 (Old No. 43), Second floor, Chamiers Road, Alwarpet, Chennai - 600018</p>
              <p style={{ fontSize: "12px", margin: "4px 0" }}><b>GST:</b> 33AAJPF7138F1ZX</p>
            </div>
          </div>

          <p style={{ marginBottom: "10px", fontFamily: "serif", fontSize: "13px" }}>
            <b>Dear Sir / Madam,</b><br />We are pleased to place this purchase order with the following details.
          </p>

          {/* ITEMS SPECIFICATION LISTS */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px", fontFamily: "serif" }}>
            <thead>
              <tr style={{ background: "#5D8F2A", color: "#fff", fontSize: "15px" }}>
                <th style={thStyle}>S.NO</th>
                <th style={{ ...thStyleLeft, width: "40%" }}>DESCRIPTION</th>
                <th style={thStyle}>UOM</th>
                <th style={thStyle}>QTY</th>
                <th style={thStyleRight}>RATE</th>
                <th style={thStyle}>GST %</th>
                <th style={thStyleRight}>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {po.items && po.items.length > 0 ? (
                po.items.map((item, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={tdStyleLeft}>{item.name}</td>
                    <td style={tdStyle}>{item.uom || "Nos"}</td>
                    <td style={tdStyle}>{item.qty}</td>
                    <td style={tdStyleRight}>{item.rate}</td>
                    <td style={tdStyle}>{Number(item.gst_percent) || 0}%</td>
                    <td style={tdStyleRight}>₹ {(item.qty * item.rate).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" style={{ padding: "20px", textAlign: "center" }}>No Items Found</td></tr>
              )}
            </tbody>
          </table>

          {/* TOTAL SECTION SUMMARY */}
          <div className="bottom-section" style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
            <div className="delivery-box" style={{ fontFamily: "serif", width: "45%" }}>
              <p style={{ fontSize: "13px", margin: "0 0 5px 0" }}><b>Delivery Address and Contact Details:</b></p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}><b>Site:</b> {po.site_name || "—"}</p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}>{po.delivery_address_line1}</p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}>{po.delivery_address_line2}</p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}>{po.delivery_address_line3}</p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}>{po.delivery_contact}</p>
              <p style={{ fontSize: "12px", margin: "2px 0" }}>{po.delivery_phone}</p>
              
              <div style={{ marginTop: "10px", paddingTop: "5px", borderTop: "1px solid #ccc" }}>
                <p style={{ fontSize: "12px", margin: "2px 0" }}>
                  <b>Payment Term:</b> {formatPaymentTerm(po)}
                </p>
                {paymentStages.length > 0 && (
                  <table style={{ width: "100%", fontSize: "11px", marginTop: "6px", borderCollapse: "collapse" }}>
                    <thead><tr><th style={{ textAlign: "left", borderBottom: "1px solid #999" }}>Stage</th><th style={{ textAlign: "right", borderBottom: "1px solid #999" }}>%</th><th style={{ textAlign: "left", borderBottom: "1px solid #999", paddingLeft: "8px" }}>Payment Term</th></tr></thead>
                    <tbody>{paymentStages.map((stage, index) => <tr key={`print-payment-stage-${index}`}><td>{stage.stage}</td><td style={{ textAlign: "right" }}>{stage.percentage}%</td><td style={{ paddingLeft: "8px" }}>{stage.term}</td></tr>)}</tbody>
                  </table>
                )}
                <p style={{ fontSize: "12px", margin: "2px 0" }}>
                  <b>Delivery Date:</b> {po.delivery_date ? new Date(po.delivery_date).toLocaleDateString("en-GB") : "As per requirement"}
                </p>
              </div>
            </div>

            <div className="totals-box" style={{ fontFamily: "serif", fontSize: "12px", width: "45%" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {discountAmount > 0 ? <><tr><td style={{ padding: "4px 0" }}>Total Base Amount</td><td style={{ textAlign: "right" }}>₹ {baseAmount.toFixed(2)}</td></tr><tr><td style={{ padding: "4px 0" }}>Overall Discount</td><td style={{ textAlign: "right" }}>- ₹ {discountAmount.toFixed(2)}</td></tr><tr><td style={{ padding: "4px 0" }}>Taxable Amount</td><td style={{ textAlign: "right" }}>₹ {subtotal.toFixed(2)}</td></tr></> : <tr><td style={{ padding: "4px 0" }}>Sub Total</td><td style={{ textAlign: "right" }}>₹ {subtotal.toFixed(2)}</td></tr>}
                  {po.tax_mode === "IGST" ? (
                    <tr><td style={{ padding: "4px 0" }}>IGST</td><td style={{ textAlign: "right" }}>₹ {igst.toFixed(2)}</td></tr>
                  ) : (
                    <><tr><td style={{ padding: "4px 0" }}>CGST</td><td style={{ textAlign: "right" }}>₹ {cgst.toFixed(2)}</td></tr><tr><td style={{ padding: "4px 0" }}>SGST</td><td style={{ textAlign: "right" }}>₹ {sgst.toFixed(2)}</td></tr></>
                  )}
                  {Number(po.tds_percent) > 0 && <tr><td style={{ padding: "4px 0" }}>TDS ({po.tds_percent}%)</td><td style={{ textAlign: "right" }}>- ₹ {tds.toFixed(2)}</td></tr>}
                  <tr className="grand-total" style={{ fontSize: "13px", fontWeight: "bold", borderTop: "1px solid #111" }}>
                    <td style={{ padding: "6px 0" }}>Grand Total</td><td style={{ textAlign: "right" }}>₹ {Math.round(total).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
              <p className="amount-words" style={{ fontSize: "12px", marginTop: "8px", fontStyle: "italic", color: "#475569" }}>
                Rupees {numberToWords(Math.floor(total))} Only
              </p>
            </div>
          </div>

          {/* CONDITIONS TERMS */}
          <div className="terms" style={{ fontFamily: "serif", fontSize: "12px", lineHeight: "1.3" }}>
            <p style={{ fontSize: "13px", margin: "5px 0" }}><b>Terms & Conditions:</b></p>
            <ul style={{ paddingLeft: "18px", margin: "0" }}>
              <li><strong>Scope of work:</strong> Supply of the above things</li>
              <li><strong>Transportation:</strong> {po.transportation || "Exclusive"}</li>
              <li><strong>Unloading:</strong> {po.unloading || "Exclusive"}</li>
              <li><strong>Test/Warranty certificates:</strong> Current dated test/warranty certificates must be submitted along with materials</li>
              <li><strong>Invoice submission:</strong> Invoice with signature required</li>
              <li><strong>Measurement:</strong> Quality issues must be replaced</li>
              <li><strong>Price escalation:</strong> No price escalation after PO issuance</li>
              <li><strong>Acknowledgment:</strong> Acknowledgment within 24 hours is mandatory</li>
            </ul>
            {additionalTerms.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <p style={{ fontSize: "13px", margin: "5px 0" }}><b>Additional Terms &amp; Conditions:</b></p>
                <ol style={{ paddingLeft: "18px", margin: "0" }}>
                  {additionalTerms.map((term, index) => <li key={`additional-print-term-${index}`}>{term}</li>)}
                </ol>
              </div>
            )}
          </div>

          {/* ALIGNED SIGNATURE SECTION */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr", 
            gap: "20px", 
            marginTop: "30px", 
            fontFamily: "serif",
            fontSize: "12px"
          }}>
            <div style={{ display: "flex", flexDirection: "column", height: "110px" }}>
              <p style={{ margin: "0", fontWeight: "bold", fontSize: "11px" }}>FOR ENSILETA INTERIORS & MODULAR SOLUTIONS</p>
              <div style={{ marginTop: "10px" }}>
                <p style={{ margin: "0", color: "#64748b", fontSize: "11px" }}>DIGITALLY SIGNED BY</p>
                <p style={{ margin: "2px 0", fontWeight: "600" }}>{po.creator_name || "Procurement Team"}</p>
                {po.creator_mobile && <p style={{ margin: "2px 0" }}>PH: {po.creator_mobile}</p>}
              </div>
              <div style={{ marginTop: "auto" }}>
                <p style={{ margin: "0", fontWeight: "bold", borderTop: "1px dashed #ccc", paddingTop: "4px" }}>AUTHORISED SIGNATORY</p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", textAlign: "right", height: "110px" }}>
              <p style={{ margin: "0", fontWeight: "bold" }}>FOR {po.vendor_name}</p>
              <div style={{ marginTop: "auto" }}>
                <p style={{ margin: "0", fontWeight: "bold", borderTop: "1px dashed #ccc", paddingTop: "4px" }}>AUTHORISED SIGNATORY</p>
              </div>
            </div>
          </div>
        
        </div>
      </div>
    </div>
  );
}

// Layout Typography Elements Definitions
const thStyle = { border: "1px solid #666", padding: "10px 8px", fontSize: "12px", textAlign: "center", background: "#5D8F2A", color: "#fff", fontWeight: "600" };
const thStyleLeft = { ...thStyle, textAlign: "left" };
const thStyleRight = { ...thStyle, textAlign: "right" };
const tdStyle = { border: "1px solid #666", padding: "8px", fontSize: "12px", textAlign: "center", verticalAlign: "top" };
const tdStyleLeft = { ...tdStyle, textAlign: "left" };
const tdStyleRight = { ...tdStyle, textAlign: "right" };

const thStyleFixes = {
  backBtn: { padding: "10px 18px", background: "#475569", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600" },
  printBtn: { padding: "10px 18px", background: "#16a34a", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" },
  sendBtn: { background: "#2563eb", color: "#fff", border: "none", padding: "10px 18px", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }
};

export default POPrintPage;
